import { Info, Tooltip } from '@silverfort/sf-components';
import { FC, useState } from 'react';
import { useServiceAccountContext } from '../../contexts/ServiceAccountContext';
import { bigNumberToText } from '../../../../../components/utils';
import { getInsightsOrderWithLicense } from './FiltersUtils';
import { ServiceAccountInsight } from '../../ServiceAccountsTypes';

type ServiceAccountsInsightProps = {
    title: string;
    count: number;
    total?: number;
    onApplyFilter: () => void;
    loading: boolean;
    main?: boolean;
    disabled?: boolean;
    info: string;
};

const ServiceAccountsInsight: FC<ServiceAccountsInsightProps> = ({
    title,
    count,
    onApplyFilter,
    loading,
    main,
    disabled,
    info
}) => {
    const { onPremNHILicenseITDREnabled, onPremNHILicenseISPMEnabled, onPremNHILicenseProtectionEnabled } =
        useServiceAccountContext();
    const colorClasses = disabled ? 'bg-sf-gray-50 ' : 'cursor-pointer';
    let spanClasses = onPremNHILicenseITDREnabled ? 'col-span-1' : disabled ? 'col-span-2' : 'col-span-1';

    if (onPremNHILicenseITDREnabled) {
        // There is a brute_force insight so all in the same size
        spanClasses = 'col-span-1';
    } else if (disabled) {
        // the total service account insight
        if (!onPremNHILicenseITDREnabled && !onPremNHILicenseISPMEnabled && !onPremNHILicenseProtectionEnabled) {
            // if there are two rows
            spanClasses = 'col-span-1';
        } else {
            // if there is one row - all should be in the same size
            spanClasses = 'col-span-2';
        }
    } else {
        spanClasses = 'col-span-1';
    }

    const countColor = main ? 'text-sf-primary-500' : '';

    return (
        <div
            className={`h-[94px] p-4 gap-2 pontier border border-sf-gray-100 rounded-lg ${colorClasses} ${spanClasses}`}
            onClick={onApplyFilter}>
            {loading ? (
                <div className="animate-pulse">
                    <div className="h-4 w-full rounded bg-sf-gray-50"></div>
                </div>
            ) : (
                <div className={`font-sf-poppins text-[21px] font-bold ${countColor}`}>{bigNumberToText(count)}</div>
            )}
            <div className="flex space-bwtween">
                <div className="text-[12px] font-medium flex-1">{title}</div>
                <div>
                    <Tooltip title={info} className="max-w-[300px]">
                        <Info />
                    </Tooltip>
                </div>
            </div>
        </div>
    );
};

const ServiceAccountsInsights = () => {
    const {
        insights,
        isFetchingInsights,
        setActiveFilters,
        onPremNHILicenseITDREnabled,
        onPremNHILicenseISPMEnabled,
        onPremNHILicenseProtectionEnabled
    } = useServiceAccountContext();

    const calcClassName =
        !onPremNHILicenseITDREnabled && !onPremNHILicenseISPMEnabled && !onPremNHILicenseProtectionEnabled
            ? 'grid grid-flow-col auto-cols-fr gap-x-4'
            : 'grid grid-cols-3 gap-x-4 gap-y-3';

    const onInsightClick = (insight: ServiceAccountInsight) => {
        setActiveFilters((prevFilters) => {
            if (!insight.filters) {
                return prevFilters;
            }
            const isInsightFilterNotActive = Object.keys(insight.filters).some((key) => {
                const filterCompareValue = JSON.stringify((insight.filters || {})[key]);
                return JSON.stringify(prevFilters[key]) !== filterCompareValue;
            });
            if (isInsightFilterNotActive) {
                return { ...prevFilters, ...insight.filters };
            } else {
                const newFilters = { ...prevFilters };
                Object.keys(insight.filters || {}).forEach((key) => {
                    delete newFilters[key];
                });
                return newFilters;
            }
        });
    };

    return (
        <div className={calcClassName}>
            {getInsightsOrderWithLicense(onPremNHILicenseITDREnabled).map((insightKey) => {
                const insight = insights[insightKey];
                return (
                    <ServiceAccountsInsight
                        key={insightKey}
                        {...insight}
                        onApplyFilter={() => onInsightClick(insight)}
                        loading={isFetchingInsights}
                    />
                );
            })}
        </div>
    );
};

export default ServiceAccountsInsights;
